import * as d3 from 'd3';
import { BarChartConfig } from '../data/BarChartConfig';
import { ChartDataType } from '../data/ChartDataType';
import { BandAxisRenderer } from './BandAxisRenderer';
import { ChartRendererContext } from './ChartRendererContext';
import { ColumnBarChartRenderer } from './ColumnBarChartRenderer';
import { LegendRenderer } from './LegendRenderer';
import { LinearAxisRenderer } from './LineAxisChartRenderer';
import { Rectangle } from './Rectange';
import { TooltipRenderer } from './TooltipRenderer';
import { BarRenderer } from './BarRenderer';
import { StackedBarRenderer } from './StackedBarRenderer';
import { ParallelBarRenderer } from './ParallelBarRenderer';

export class BarChartRenderer extends ColumnBarChartRenderer {
  constructor(protected config: BarChartConfig) {
    super(config);
  }

  render(el: HTMLElement, context: ChartRendererContext) {
    // change this later when mnb-chart passes in ele[0] instead of ele
    const temp: any = el;
    const ele: HTMLElement = temp.length ? temp[0] : temp;
    this.ele = ele;

    this.svgElement = this.createSvgElement(ele);
    this.svgHTMLElement = this.svgElement.node()!;

    const labelPaddingLeft = this.config.xAxisConfig.hide ? 0 : 15;
    const labelPaddingRight = this.config.xAxisConfig.hide ? 0 : this.rendererConfig.hasSecondaryAxis() ? 20 : 10;

    const dimensions: { height: number; width: number } = ColumnBarChartRenderer.getDimensions(ele);

    let legendHeight = 0;
    if (!this.config.legendConfig.hide) {
      legendHeight = new LegendRenderer(context, this.config).render(this.svgElement, this.config.data!, dimensions);
    }

    const chartRect: Rectangle = {
      x: 0,
      y: 0,
      width: dimensions.width - labelPaddingRight,
      height: dimensions.height - legendHeight,
    };

    this.gElement = this.svgElement.append('g');

    const yAxisRenderer = new BandAxisRenderer(this.gElement, context, this.config, true);
    const xAxisRenderer = new LinearAxisRenderer(
      this.gElement,
      context,
      this.config,
      false,
      this.rendererConfig.hasSecondaryAxis()
    );

    const xAxisHeight = xAxisRenderer.calcAxisHeight();
    let secondaryXAxisHeight = 0;

    const secondaryXAxisRenderer = new LinearAxisRenderer(
      this.gElement,
      context,
      this.config,
      true,
      this.rendererConfig.hasSecondaryAxis()
    );
    if (this.rendererConfig.hasSecondaryAxis()) {
      secondaryXAxisHeight = secondaryXAxisRenderer.calcAxisHeight();
    }

    const innerHeight = chartRect.height - xAxisHeight - secondaryXAxisHeight;

    const dataLimit = yAxisRenderer.calcMaximumBands(innerHeight, this.rendererConfig);

    const yAxis = yAxisRenderer.renderY(innerHeight, chartRect.width, dataLimit);

    const innerRect: Rectangle = {
      x: chartRect.x + Math.max(yAxis.width, labelPaddingLeft),
      y: chartRect.y + secondaryXAxisHeight,
      width: chartRect.width - Math.max(yAxis.width, labelPaddingLeft),
      height: innerHeight,
    };

    this.gElement.attr('transform', `translate(${innerRect.x},${innerRect.y})`);

    const xAxis = xAxisRenderer.renderX(innerRect.width, innerRect.height, dataLimit, true);
    let secondaryXAxis: { scale: d3.ScaleLinear<number, number> };
    if (this.rendererConfig.hasSecondaryAxis()) {
      secondaryXAxis = secondaryXAxisRenderer.renderX(innerRect.width, innerRect.height, dataLimit, true);
    }

    const bars: BarRenderer[] = [];

    // comparison series
    if (this.rendererConfig.hasComparisonData()) {
      // primary
      bars.push(
        new BarRenderer(
          this.gElement,
          this.rendererConfig.getComparisonData()!,
          this.rendererConfig.hasComparisonData(),
          this.rendererConfig.hasSecondaryData()
        )
      );
      if (this.rendererConfig.hasSecondaryData()) {
        bars.push(
          new BarRenderer(
            this.gElement,
            this.rendererConfig.getSecondaryComparisonData()!,
            this.rendererConfig.hasComparisonData(),
            this.rendererConfig.hasSecondaryData(),
            this.rendererConfig.hasSecondaryAxis()
          )
        );
      }
    }

    if (this.config.breakdownMode) {
      // render with breakdown data. breakdown doesn't render any comparison
      switch (this.config.breakdownMode) {
        case 'stacked':
          const stackedRenderer = new StackedBarRenderer(this.gElement, false);
          stackedRenderer.render(
            this.config.getDataArrays(ChartDataType.breakdown),
            xAxis.scale,
            yAxis.scale,
            dataLimit
          );
          break;
        case 'fill-stacked':
          const fillStackedRenderer = new StackedBarRenderer(this.gElement, true);
          fillStackedRenderer.render(
            this.config.getDataArrays(ChartDataType.breakdown),
            xAxis.scale,
            yAxis.scale,
            dataLimit
          );
          break;
        case 'parallel':
          const parallelRenderer = new ParallelBarRenderer(this.gElement);
          parallelRenderer.render(this.config.data!, xAxis.scale, yAxis.scale);

          break;
        default:
          console.error('breakdownData without a configured breakdownMode');
      }
    } else {
      // primary
      bars.push(
        new BarRenderer(
          this.gElement,
          this.rendererConfig.getPrimaryData()!,
          this.rendererConfig.hasComparisonData(),
          this.rendererConfig.hasSecondaryData()
        )
      );
      if (this.rendererConfig.hasSecondaryData()) {
        bars.push(
          new BarRenderer(
            this.gElement,
            this.rendererConfig.getSecondaryData()!,
            this.rendererConfig.hasComparisonData(),
            this.rendererConfig.hasSecondaryData(),
            this.rendererConfig.hasSecondaryAxis()
          )
        );
      }

      if (this.rendererConfig.hasPlanData()) {
        bars.push(
          new BarRenderer(
            this.gElement,
            this.rendererConfig.getPrimaryPlanData()!,
            this.rendererConfig.hasComparisonData(),
            this.rendererConfig.hasSecondaryData()
          )
        );
      }
      if (this.rendererConfig.hasSecondaryData() && this.rendererConfig.getSecondaryPlanData()) {
        bars.push(
          new BarRenderer(
            this.gElement,
            this.rendererConfig.getSecondaryPlanData()!,
            this.rendererConfig.hasComparisonData(),
            this.rendererConfig.hasSecondaryData(),
            this.rendererConfig.hasSecondaryAxis()
          )
        );
      }
    }

    bars.forEach(bar => bar.render(!bar.secondaryAxis ? xAxis.scale : secondaryXAxis.scale, yAxis.scale, dataLimit));

    if (this.rendererConfig.hasAverageData()) {
      const average = this.rendererConfig.getAverageData()!;
      const avgData = average.getData()[0];
      const avgValue = avgData?.value!;
      const avgX = xAxis.scale(avgValue);
      this.gElement
        .append('line')
        .attr('x1', avgX)
        .attr('x2', avgX)
        .attr('y1', innerRect.y)
        .attr('y2', innerRect.height - 8)
        .attr('class', 'avg-line');

      this.gElement
        .append('text')
        .attr('x', avgX)
        .attr('y', innerRect.height + 4)
        .attr('text-anchor', 'middle')
        .text(average.label + ' ' + context.measureFormatter(avgValue, this.config.value?.unit!))
        .attr('class', 'avg-line-label');
    }

    new TooltipRenderer(this.config, this.tooltipContent!, bars).render(
      this.svgElement,
      innerRect,
      yAxis.scale,
      xAxis.scale,
      dataLimit,
      true,
      true
    );
  }
}
